import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { CardTitleWithHelp, HoverHintContent, PortaledHoverHint, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Input, Label } from "@/components/ui/Input";
import {
  buildMrpSkuDetailPath,
  type MrpAmlEntry,
  type MrpAmlShoppingPref,
  type MrpAmlSourcingSupplierLink,
  type MrpShoppingCpnResponse,
  type MrpShoppingDiscoverDistributorLinksResult,
  type MrpShoppingListing,
  type MrpShoppingPriceBreak,
  type MrpShoppingSupplierChannelRow,
} from "@/lib/mrp";
import { createHubPurchaseOrderAndNavigate } from "./purchaseOrderHubDraftNav";
import { sortAmlAlternateRows } from "@/pages/mrp/aml/amlEntryUtils";
import {
  computePointAnchoredContextMenu,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";

function octopartCatalogHref(mpn: string): string {
  return `https://octopart.com/search?q=${encodeURIComponent(mpn)}`;
}

function sortClassifiedShoppingSuppliers(
  rows: MrpShoppingSupplierChannelRow[],
): (MrpShoppingSupplierChannelRow & { channel: NonNullable<MrpShoppingSupplierChannelRow["channel"]> })[] {
  const classified = rows.filter(
    (row): row is MrpShoppingSupplierChannelRow & { channel: NonNullable<MrpShoppingSupplierChannelRow["channel"]> } =>
      row.channel != null,
  );
  classified.sort((left, right) => {
    const leftRank = left.channel === "catalog_web" ? 0 : 1;
    const rightRank = right.channel === "catalog_web" ? 0 : 1;
    if (leftRank !== rightRank) return leftRank - rightRank;
    return (left.mv_supplier_name ?? "").localeCompare(right.mv_supplier_name ?? "", undefined, {
      sensitivity: "base",
    });
  });
  return classified;
}

function linksForAml(links: MrpAmlSourcingSupplierLink[], amlId: number): MrpAmlSourcingSupplierLink[] {
  return links.filter((row) => row.approved_component_id === amlId);
}

function supplierChannelRowsFromAmlLinks(linkRows: MrpAmlSourcingSupplierLink[]): MrpShoppingSupplierChannelRow[] {
  return linkRows.map((row) => ({
    id: row.id,
    mv_supplier_id: row.mv_supplier_id,
    mv_supplier_name: row.mv_supplier_name,
    channel: row.channel,
  }));
}

/** Distinct supplier columns for the matrix (same supplier can appear on multiple AML rows with same channel → one column). */
function uniqueMatrixSupplierColumns(links: MrpAmlSourcingSupplierLink[]): MrpShoppingSupplierChannelRow[] {
  const map = new Map<string, MrpShoppingSupplierChannelRow>();
  for (const row of links) {
    const key = `${row.id}:${row.channel}`;
    if (!map.has(key)) {
      map.set(key, {
        id: row.id,
        mv_supplier_id: row.mv_supplier_id,
        mv_supplier_name: row.mv_supplier_name,
        channel: row.channel,
      });
    }
  }
  return sortClassifiedShoppingSuppliers(
    [...map.values()] as (MrpShoppingSupplierChannelRow & {
      channel: NonNullable<MrpShoppingSupplierChannelRow["channel"]>;
    })[],
  );
}

function amlMpnSearchTerm(aml: MrpAmlEntry): string {
  return (aml.mpn ?? "").trim();
}

function findListing(
  listings: MrpShoppingListing[],
  approvedComponentId: number,
  supplierDataId: number,
): MrpShoppingListing | undefined {
  return listings.find(
    (listing) =>
      listing.approved_component_id === approvedComponentId &&
      listing.supplier_data_id === supplierDataId,
  );
}

/** Lowest break-quantity tier (same basis as “Add to PO” / stage line). */
function lowestPriceBreakForListing(listing: MrpShoppingListing | undefined): MrpShoppingPriceBreak | null {
  if (!listing?.price_breaks.length) return null;
  const sorted = [...listing.price_breaks].sort((first, second) => first.break_quantity - second.break_quantity);
  return sorted[0] ?? null;
}

function supplierSourcingChannelLabel(channel: MrpShoppingSupplierChannelRow["channel"]): string {
  if (channel === "catalog_web") return "Catalog webstore";
  if (channel === "contract_manufacturer") return "Contract manufacturer";
  return "—";
}

function formatShoppingTimestamp(iso: string | null | undefined): string {
  if (iso == null || iso === "") return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

/** Shown when AML row is approved but `active` is false (AML detail uses include_inactive; shopping lists these too). */
function AmlInactiveBadge({ active }: { active: boolean }) {
  if (active) return null;
  return (
    <HoverHintContent hoverHint="Inactive is a listing flag only. This row is still Status approved, so it appears here for listings and sourcing. Deprecated parts are omitted from shopping.">
      <span className="inline-flex shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
        Inactive on AML
      </span>
    </HoverHintContent>
  );
}

function bestViewedUnitPrice(listings: MrpShoppingListing[]): number | null {
  let best: number | null = null;
  for (const listing of listings) {
    for (const tier of listing.price_breaks) {
      if (best === null || tier.unit_price < best) {
        best = tier.unit_price;
      }
    }
  }
  return best;
}

type ListingDraft = {
  product_url: string;
  purchaser_notes: string;
  is_preferred_for_buy: boolean;
  offers_product_observed_at: string;
  availability_note: string;
  price_breaks: Omit<MrpShoppingPriceBreak, "id">[];
};

function emptyTier(): Omit<MrpShoppingPriceBreak, "id"> {
  const now = new Date();
  return {
    break_quantity: 1,
    unit_price: 0,
    currency: "USD",
    observed_at: now.toISOString().slice(0, 16),
    note: null,
  };
}

function listingToDraft(listing: MrpShoppingListing | undefined): ListingDraft {
  if (!listing) {
    return {
      product_url: "",
      purchaser_notes: "",
      is_preferred_for_buy: false,
      offers_product_observed_at: "",
      availability_note: "",
      price_breaks: [emptyTier()],
    };
  }
  return {
    product_url: listing.product_url ?? "",
    purchaser_notes: listing.purchaser_notes ?? "",
    is_preferred_for_buy: listing.is_preferred_for_buy,
    offers_product_observed_at: listing.offers_product_observed_at
      ? listing.offers_product_observed_at.slice(0, 16)
      : "",
    availability_note: listing.availability_note ?? "",
    price_breaks:
      listing.price_breaks.length > 0
        ? listing.price_breaks.map((tier) => ({
            break_quantity: tier.break_quantity,
            unit_price: tier.unit_price,
            currency: tier.currency,
            observed_at: tier.observed_at.slice(0, 16),
            note: tier.note,
          }))
        : [emptyTier()],
  };
}

type ShoppingMatrixContextMenu =
  | null
  | {
      x: number;
      y: number;
      kind: "catalog";
      href: string;
      portalLabel: string;
      amlId: number;
    }
  | {
      x: number;
      y: number;
      kind: "supplier";
      amlId: number;
      supplierId: number;
      productUrl: string | null;
      listing: MrpShoppingListing | undefined;
    }
  | {
      x: number;
      y: number;
      kind: "part";
      amlId: number;
    };

type ShoppingLogPriceModal =
  | null
  | { kind: "no-suppliers"; aml: MrpAmlEntry }
  | { kind: "pick-supplier"; aml: MrpAmlEntry; supplierId: number };

function ShoppingSourcingMatrixTable({
  cpn,
  amlRows,
  amlSuppliers,
  amlShoppingPrefs,
  listings,
  setEditKey,
  setDraft,
  copyText,
  stageLine,
  hubWritesAllowed,
  hubReadOnlyTitle,
}: {
  cpn: string;
  amlRows: MrpAmlEntry[];
  amlSuppliers: MrpAmlSourcingSupplierLink[];
  amlShoppingPrefs: MrpAmlShoppingPref[];
  listings: MrpShoppingListing[];
  setEditKey: (key: string | null) => void;
  setDraft: (draft: ListingDraft | null) => void;
  copyText: (label: string, text: string) => Promise<void>;
  stageLine: (body: {
    cpn: string;
    supplier_data_id: number;
    approved_component_id: number;
    quantity: number;
    unit_price: number | null;
  }) => void;
  hubWritesAllowed: boolean;
  hubReadOnlyTitle: string;
}) {
  const readOnlyMessage =
    hubReadOnlyTitle.trim() !== "" ? hubReadOnlyTitle : "Your role has read-only access to the MRP hub module.";
  const columnSuppliers = useMemo(() => uniqueMatrixSupplierColumns(amlSuppliers), [amlSuppliers]);
  const octopartByAmlId = useMemo(() => {
    const map = new Map<number, boolean>();
    for (const preference of amlShoppingPrefs) {
      map.set(preference.approved_component_id, preference.show_octopart);
    }
    return map;
  }, [amlShoppingPrefs]);
  const showOctopartColumn = amlRows.some((row) => octopartByAmlId.get(row.id) === true);
  const matrixHintAnchorRef = useRef<HTMLElement | null>(null);
  const [matrixHintText, setMatrixHintText] = useState<string | null>(null);
  const [matrixHintRevision, setMatrixHintRevision] = useState(0);
  const showMatrixCellHint = useCallback((anchor: HTMLElement, hintText: string) => {
    matrixHintAnchorRef.current = anchor;
    setMatrixHintText(hintText);
    setMatrixHintRevision((revision) => revision + 1);
  }, []);
  const clearMatrixCellHint = useCallback(() => {
    matrixHintAnchorRef.current = null;
    setMatrixHintText(null);
  }, []);
  const [menu, setMenu] = useState<ShoppingMatrixContextMenu>(null);
  const [logPriceModal, setLogPriceModal] = useState<ShoppingLogPriceModal>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuBox, setMenuBox] = useState<AnchoredMenuPanelBox | null>(null);

  const repositionContextMenu = useCallback(() => {
    if (!menu) {
      setMenuBox(null);
      return;
    }
    setMenuBox(
      computePointAnchoredContextMenu({
        clientX: menu.x,
        clientY: menu.y,
        menuWidth: 13 * 16,
        maxHeightCap: Math.min(440, Math.floor(window.innerHeight * 0.85)),
      }),
    );
  }, [menu]);

  useLayoutEffect(() => {
    if (!menu) {
      setMenuBox(null);
      return;
    }
    clearMatrixCellHint();
    repositionContextMenu();
    const onLayout = (): void => {
      repositionContextMenu();
    };
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    window.visualViewport?.addEventListener("resize", onLayout);
    window.visualViewport?.addEventListener("scroll", onLayout);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
      window.visualViewport?.removeEventListener("resize", onLayout);
      window.visualViewport?.removeEventListener("scroll", onLayout);
    };
  }, [menu, repositionContextMenu, clearMatrixCellHint]);

  const openLogPriceFlow = (aml: MrpAmlEntry): void => {
    const rowSuppliers = sortClassifiedShoppingSuppliers(
      supplierChannelRowsFromAmlLinks(linksForAml(amlSuppliers, aml.id)) as (MrpShoppingSupplierChannelRow & {
        channel: NonNullable<MrpShoppingSupplierChannelRow["channel"]>;
      })[],
    );
    if (rowSuppliers.length === 0) {
      setLogPriceModal({ kind: "no-suppliers", aml });
      return;
    }
    if (rowSuppliers.length === 1) {
      const supplierRow = rowSuppliers[0];
      setEditKey(`${aml.id}-${supplierRow.id}`);
      setDraft(listingToDraft(findListing(listings, aml.id, supplierRow.id)));
      return;
    }
    setLogPriceModal({
      kind: "pick-supplier",
      aml,
      supplierId: rowSuppliers[0].id,
    });
  };

  useEffect(() => {
    if (!menu) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (menuRef.current?.contains(event.target as Node)) return;
      setMenu(null);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setMenu(null);
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onMouseDown);
    }, 0);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menu]);

  const totalCols = 1 + (showOctopartColumn ? 1 : 0) + columnSuppliers.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sourcing matrix</CardTitle>
      </CardHeader>
      <CardBody className="overflow-x-auto">
        <>
        <table
          className="min-w-full border-collapse border border-slate-200 text-sm"
          onMouseLeave={clearMatrixCellHint}
        >
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="sticky left-0 z-10 min-w-[10rem] border-b border-r border-slate-200 bg-slate-50 px-2 py-2">
                Part
              </th>
              {showOctopartColumn ? (
                <th className="border-b border-slate-200 px-2 py-2">Octopart</th>
              ) : null}
              {columnSuppliers.map((supplierRow) => (
                <th
                  key={`${supplierRow.id}-${supplierRow.channel}`}
                  className="min-w-[7.5rem] border-b border-slate-200 px-2 py-2 align-bottom font-semibold normal-case text-slate-700"
                >
                  <div className="text-xs">{supplierRow.mv_supplier_name ?? `Supplier #${supplierRow.id}`}</div>
                  <div className="mt-0.5 text-[10px] font-normal normal-case text-slate-500">
                    {supplierSourcingChannelLabel(supplierRow.channel)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {amlRows.length === 0 ? (
              <tr>
                <td className="px-2 py-3 text-slate-600" colSpan={totalCols}>
                  No approved AML rows for this CPN. Add alternates on the{" "}
                  <Link className="text-sky-700 underline hover:text-sky-900" to={buildMrpSkuDetailPath(cpn, { tab: "aml" })}>
                    AML detail
                  </Link>{" "}
                  tab.
                </td>
              </tr>
            ) : (
              amlRows.map((aml) => {
                const mpnTerm = amlMpnSearchTerm(aml);
                return (
                  <tr key={aml.id} className="border-b border-slate-100">
                    <td
                      className="sticky left-0 z-10 cursor-context-menu border-r border-slate-200 bg-white px-2 py-2 align-top shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]"
                      onContextMenu={(event) => {
                        event.preventDefault();
                        setMenu({
                          kind: "part",
                          x: event.clientX,
                          y: event.clientY,
                          amlId: aml.id,
                        });
                      }}
                    >
                      <div className="font-mono text-xs text-slate-900">
                        {mpnTerm.length > 0 ? mpnTerm : <span className="text-amber-800">(no MPN)</span>}
                      </div>
                      <div className="text-xs text-slate-600">{aml.manufacturer_name?.trim() || "—"}</div>
                      <AmlInactiveBadge active={aml.active} />
                    </td>
                    {showOctopartColumn ? (
                      <td className="border-r border-slate-100 p-0 align-top">
                        {octopartByAmlId.get(aml.id) ? (
                          mpnTerm.length > 0 ? (
                            <a
                              href={octopartCatalogHref(mpnTerm)}
                              target="_blank"
                              rel="noreferrer"
                              className="flex min-h-[3.5rem] cursor-help items-center justify-center px-2 py-2 text-center text-sm font-medium text-sky-800 hover:bg-sky-50"
                              onPointerEnter={(event) => {
                                const cell = event.currentTarget.closest("td");
                                if (cell instanceof HTMLElement) {
                                  showMatrixCellHint(cell, octopartCatalogHref(mpnTerm));
                                }
                              }}
                              onContextMenu={(event) => {
                                event.preventDefault();
                                const href = octopartCatalogHref(mpnTerm);
                                setMenu({
                                  x: event.clientX,
                                  y: event.clientY,
                                  kind: "catalog",
                                  href,
                                  portalLabel: "Octopart",
                                  amlId: aml.id,
                                });
                              }}
                            >
                              Open
                            </a>
                          ) : (
                            <div
                              className="flex min-h-[3.5rem] cursor-context-menu items-center justify-center px-2 py-2 text-center text-xs text-amber-900"
                              onContextMenu={(event) => {
                                event.preventDefault();
                                setMenu({
                                  kind: "part",
                                  x: event.clientX,
                                  y: event.clientY,
                                  amlId: aml.id,
                                });
                              }}
                            >
                              Need MPN
                            </div>
                          )
                        ) : (
                          <div
                            className="flex min-h-[3.5rem] cursor-help items-center justify-center px-2 py-2 text-center text-xs text-slate-400"
                            onPointerEnter={(event) => {
                              const cell = event.currentTarget.closest("td");
                              if (cell instanceof HTMLElement) {
                                showMatrixCellHint(
                                  cell,
                                  "Enable Octopart for this alternate on the AML detail tab.",
                                );
                              }
                            }}
                          >
                            —
                          </div>
                        )}
                      </td>
                    ) : null}
                    {columnSuppliers.map((supplierRow) => {
                      const rowLinked = linksForAml(amlSuppliers, aml.id).some(
                        (linkRow) => linkRow.id === supplierRow.id && linkRow.channel === supplierRow.channel,
                      );
                      if (!rowLinked) {
                        return (
                          <td
                            key={`${aml.id}-${supplierRow.id}-${supplierRow.channel}`}
                            className="border-r border-slate-100 bg-slate-50/50 p-0 align-top last:border-r-0"
                            onPointerEnter={(event) => {
                              showMatrixCellHint(
                                event.currentTarget,
                                "Not a vendor for this alternate — add on the AML detail tab for this MPN row.",
                              );
                            }}
                          >
                            <div className="flex min-h-[3.5rem] cursor-help items-center justify-center px-2 py-2 text-center text-xs text-slate-400">
                              —
                            </div>
                          </td>
                        );
                      }
                      const listing = findListing(listings, aml.id, supplierRow.id);
                      const tier = lowestPriceBreakForListing(listing);
                      const productUrl = listing?.product_url?.trim() ? listing.product_url.trim() : null;
                      const cellInner = (
                        <div className="space-y-1 text-xs">
                          {listing?.is_preferred_for_buy ? (
                            <span className="inline-block rounded bg-emerald-100 px-1 text-[10px] font-medium text-emerald-900">
                              Preferred
                            </span>
                          ) : null}
                          {tier != null ? (
                            <div className="font-mono text-slate-900">
                              {tier.currency} {tier.unit_price.toFixed(4)}
                              <span className="block font-sans text-[10px] font-normal text-slate-600">
                                qty ≥ {tier.break_quantity}
                              </span>
                            </div>
                          ) : (
                            <div className="text-slate-500">No saved price</div>
                          )}
                          <div className="text-[10px] leading-tight text-slate-500">
                            Observed {formatShoppingTimestamp(tier?.observed_at ?? listing?.offers_product_observed_at ?? null)}
                          </div>
                          <div className="text-[10px] text-slate-500">
                            Saved {listing != null ? formatShoppingTimestamp(listing.updated_at) : "—"}
                          </div>
                        </div>
                      );
                      const openSupplierMenu = (event: ReactMouseEvent<HTMLAnchorElement | HTMLDivElement>): void => {
                        event.preventDefault();
                        setMenu({
                          x: event.clientX,
                          y: event.clientY,
                          kind: "supplier",
                          amlId: aml.id,
                          supplierId: supplierRow.id,
                          productUrl,
                          listing,
                        });
                      };
                      return (
                        <td
                          key={`${aml.id}-${supplierRow.id}`}
                          className="border-r border-slate-100 p-0 align-top last:border-r-0"
                          onPointerEnter={
                            productUrl
                              ? (event) => {
                                  showMatrixCellHint(event.currentTarget, productUrl);
                                }
                              : undefined
                          }
                        >
                          {productUrl ? (
                            <a
                              href={productUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="block min-h-[3.5rem] cursor-help px-2 py-2 text-slate-800 hover:bg-slate-50"
                              onContextMenu={openSupplierMenu}
                            >
                              {cellInner}
                            </a>
                          ) : (
                            <div
                              className="min-h-[3.5rem] cursor-context-menu px-2 py-2 text-slate-800"
                              onContextMenu={openSupplierMenu}
                            >
                              {cellInner}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <PortaledHoverHint
          open={matrixHintText != null}
          anchorRef={matrixHintAnchorRef}
          anchorRevision={matrixHintRevision}
        >
          {matrixHintText}
        </PortaledHoverHint>
        </>
        {amlRows.length > 0 && columnSuppliers.length === 0 ? (
          <p className="mt-3 text-sm text-slate-600">
            {showOctopartColumn
              ? "Add Megaventory suppliers per alternate on the "
              : "No supplier columns yet. Add Megaventory suppliers (and optionally Octopart) per alternate on the "}
            <Link className="text-sky-700 underline hover:text-sky-900" to={buildMrpSkuDetailPath(cpn, { tab: "aml" })}>
              AML detail
            </Link>{" "}
            tab.
          </p>
        ) : null}
      </CardBody>

      {menu && menuBox
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              style={{
                left: menuBox.left,
                top: menuBox.top,
                width: menuBox.width,
                maxHeight: menuBox.maxHeight,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
            >
          {menu.kind === "catalog" ? (
            <>
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                onClick={() => {
                  window.open(menu.href, "_blank", "noopener,noreferrer");
                  setMenu(null);
                }}
              >
                Open {menu.portalLabel} in new tab
              </button>
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                onClick={() => {
                  void copyText(`${menu.portalLabel} link`, menu.href);
                  setMenu(null);
                }}
              >
                Copy link
              </button>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyMessage} layout="block">
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!hubWritesAllowed}
                  onClick={() => {
                    const row = amlRows.find((alternateRow) => alternateRow.id === menu.amlId);
                    if (row) openLogPriceFlow(row);
                    setMenu(null);
                  }}
                >
                  Log saved price…
                </button>
              </ReadOnlyHoverLayer>
            </>
          ) : menu.kind === "part" ? (
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyMessage} layout="block">
              <button
                type="button"
                role="menuitem"
                className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!hubWritesAllowed}
                onClick={() => {
                  const row = amlRows.find((alternateRow) => alternateRow.id === menu.amlId);
                  if (row) openLogPriceFlow(row);
                  setMenu(null);
                }}
              >
                Log saved price…
              </button>
            </ReadOnlyHoverLayer>
          ) : (
            <>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyMessage} layout="block">
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!hubWritesAllowed}
                  onClick={() => {
                    setEditKey(`${menu.amlId}-${menu.supplierId}`);
                    setDraft(listingToDraft(menu.listing));
                    setMenu(null);
                  }}
                >
                  Log saved price…
                </button>
              </ReadOnlyHoverLayer>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyMessage} layout="block">
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!hubWritesAllowed}
                  onClick={() => {
                    if (!hubWritesAllowed) return;
                    const tier = lowestPriceBreakForListing(menu.listing);
                    stageLine({
                      cpn,
                      supplier_data_id: menu.supplierId,
                      approved_component_id: menu.amlId,
                      quantity: tier?.break_quantity ?? 1,
                      unit_price: tier?.unit_price ?? null,
                    });
                    setMenu(null);
                  }}
                >
                  Stage line
                </button>
              </ReadOnlyHoverLayer>
              {menu.productUrl ? (
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                  onClick={() => {
                    window.open(menu.productUrl!, "_blank", "noopener,noreferrer");
                    setMenu(null);
                  }}
                >
                  Open product URL
                </button>
              ) : null}
            </>
          )}
            </div>,
            document.body,
          )
        : null}

      {logPriceModal?.kind === "no-suppliers" ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onClick={() => setLogPriceModal(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="shopping-log-price-no-suppliers-title"
            className="max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="shopping-log-price-no-suppliers-title" className="text-base font-semibold text-slate-900">
              Log saved price
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Add at least one Megaventory supplier to <strong className="font-normal">this alternate</strong> under{" "}
              <strong className="font-normal">Sourcing vendors</strong> on the AML detail tab, then right-click again to
              open the price form.
            </p>
            <Button type="button" className="mt-4" size="sm" variant="secondary" onClick={() => setLogPriceModal(null)}>
              Close
            </Button>
          </div>
        </div>
      ) : null}

      {logPriceModal?.kind === "pick-supplier" ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 p-4"
          role="presentation"
          onClick={() => setLogPriceModal(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="shopping-log-price-pick-title"
            className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="shopping-log-price-pick-title" className="text-base font-semibold text-slate-900">
              Log saved price
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Alternate:{" "}
              <span className="font-mono text-xs text-slate-900">
                {logPriceModal.aml.mpn?.trim() || "—"} · {logPriceModal.aml.manufacturer_name?.trim() || "—"}
              </span>
            </p>
            <div className="mt-3">
              <Label>Supplier</Label>
              <select
                className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
                value={logPriceModal.supplierId}
                onChange={(event) =>
                  setLogPriceModal((previous) =>
                    previous?.kind === "pick-supplier"
                      ? { ...previous, supplierId: Number.parseInt(event.target.value, 10) }
                      : previous,
                  )
                }
              >
                {sortClassifiedShoppingSuppliers(
                  supplierChannelRowsFromAmlLinks(linksForAml(amlSuppliers, logPriceModal.aml.id)) as (MrpShoppingSupplierChannelRow & {
                    channel: NonNullable<MrpShoppingSupplierChannelRow["channel"]>;
                  })[],
                ).map((supplierRow) => (
                  <option key={`${supplierRow.id}-${supplierRow.channel}`} value={supplierRow.id}>
                    {supplierRow.mv_supplier_name ?? `Supplier #${supplierRow.id}`} (
                    {supplierSourcingChannelLabel(supplierRow.channel)})
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyMessage} layout="inline">
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  disabled={!hubWritesAllowed}
                  onClick={() => {
                    if (!hubWritesAllowed) return;
                    const { aml, supplierId } = logPriceModal;
                    setEditKey(`${aml.id}-${supplierId}`);
                    setDraft(listingToDraft(findListing(listings, aml.id, supplierId)));
                    setLogPriceModal(null);
                  }}
                >
                  Continue to price form
                </Button>
              </ReadOnlyHoverLayer>
              <Button type="button" size="sm" variant="ghost" onClick={() => setLogPriceModal(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

export function SkuShoppingTab({ cpn }: { cpn: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const mrpReadOnlyTitle = "Your role has read-only access to the MRP hub module.";

  const [editKey, setEditKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<ListingDraft | null>(null);

  const shoppingQuery = useQuery<MrpShoppingCpnResponse, ApiError>({
    queryKey: ["mrp", "shopping", "cpn", cpn],
    queryFn: () =>
      apiFetch<MrpShoppingCpnResponse>(`/api/mrp/shopping/cpn/${encodeURIComponent(cpn)}`),
    enabled: cpn.length > 0,
  });

  const stagedQuery = useQuery<{ rows: import("@/lib/mrp").MrpShoppingStagedLine[] }, ApiError>({
    queryKey: ["mrp", "shopping", "staged"],
    queryFn: () => apiFetch("/api/mrp/shopping/staged"),
    enabled: cpn.length > 0,
    refetchOnWindowFocus: true,
  });

  const discoverDistributorLinksMutation = useMutation({
    mutationFn: () =>
      apiFetch<MrpShoppingDiscoverDistributorLinksResult>("/api/mrp/shopping/discover-distributor-links", {
        method: "POST",
        body: { cpn, dry_run: false },
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["mrp", "shopping", "cpn", cpn] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml"] });
      const directApplied = result.direct.applied.length;
      const octoApplied = result.octopart?.applied.length ?? 0;
      const parts = [
        `Applied ${directApplied} direct API link row(s) (DigiKey/Mouser URLs, stock notes, and price breaks when empty).`,
        octoApplied > 0
          ? `Applied ${octoApplied} Octopart link row(s) (Future and other sellers; Octopart column when matched).`
          : result.octopart == null
            ? "Octopart was not configured; Future links need OCTOPART_API_TOKEN."
            : null,
        result.direct.skipped.length + (result.octopart?.skipped.length ?? 0) > 0
          ? `Some alternates were skipped (no API match or supplier not in Megaventory).`
          : null,
        result.warnings.length > 0
          ? `Warnings (first 10):\n${result.warnings.slice(0, 10).join("\n")}`
          : null,
      ].filter(Boolean);
      window.alert(parts.join("\n\n"));
    },
  });

  const putListingMutation = useMutation({
    mutationFn: (body: {
      approved_component_id: number;
      supplier_data_id: number;
      product_url: string | null;
      purchaser_notes: string | null;
      is_preferred_for_buy: boolean;
      offers_product_observed_at: string | null;
      availability_note: string | null;
      price_breaks: {
        break_quantity: number;
        unit_price: number;
        currency: string;
        observed_at: string;
        note: string | null;
      }[];
    }) => apiFetch("/api/mrp/shopping/listing", { method: "PUT", body }),
    onSuccess: async () => {
      setEditKey(null);
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: ["mrp", "shopping", "cpn", cpn] });
    },
  });

  const postStagedMutation = useMutation({
    mutationFn: (body: {
      cpn: string;
      supplier_data_id: number;
      approved_component_id: number;
      quantity: number;
      unit_price: number | null;
    }) => apiFetch("/api/mrp/shopping/staged", { method: "POST", body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mrp", "shopping", "staged"] });
    },
  });

  const deleteStagedMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/mrp/shopping/staged/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mrp", "shopping", "staged"] });
    },
  });

  const clearStagedMutation = useMutation({
    mutationFn: () => apiFetch("/api/mrp/shopping/staged", { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["mrp", "shopping", "staged"] });
    },
  });

  const data = shoppingQuery.data;
  const amlSorted = useMemo(() => (data ? sortAmlAlternateRows(data.aml_rows as MrpAmlEntry[]) : []), [data]);
  const bestViewed = data ? bestViewedUnitPrice(data.listings) : null;
  const lastPurchase = data?.org_buying_history.last_purchase ?? null;

  const openPoWithStaged = (): void => {
    if (!hubWritesAllowed) return;
    const rows = stagedQuery.data?.rows ?? [];
    if (rows.length === 0) return;
    const supplierId = rows[0]!.supplier_data_id;
    if (rows.some((row) => row.supplier_data_id !== supplierId)) {
      window.alert("Staged lines use more than one supplier. Remove lines or keep a single supplier for one PO.");
      return;
    }
    const lines = rows.map((row) => ({
      sku: row.cpn,
      quantity: String(row.quantity),
      unit_price: row.unit_price == null ? undefined : String(row.unit_price),
    }));
    void createHubPurchaseOrderAndNavigate(navigate, {
      location_code: "sgs",
      supplier_id: supplierId,
      lines,
    });
  };

  const copyText = async (label: string, text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      window.alert(`Could not copy ${label}.`);
    }
  };

  if (shoppingQuery.isLoading) {
    return <p className="text-sm text-slate-500">Loading shopping…</p>;
  }
  if (shoppingQuery.error) {
    return (
      <p className="text-sm text-rose-700">
        {shoppingQuery.error.message}
        {shoppingQuery.error.message.includes("migrations") ? (
          <>
            {" "}
            Run <code className="rounded bg-slate-100 px-1 text-xs">npm run migrate</code> after resolving any
            migration checksum issues.
          </>
        ) : null}
      </p>
    );
  }
  if (!data) {
    return null;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
            <div>
              <span className="text-slate-500">Last org purchase: </span>
              {lastPurchase ? (
                <span>
                  {lastPurchase.supplier_name ?? "—"} ·{" "}
                  {lastPurchase.unit_price != null ? `$${lastPurchase.unit_price.toFixed(4)}` : "—"} · qty{" "}
                  {lastPurchase.order_quantity ?? "—"} · {lastPurchase.mv_number ?? `MV ${lastPurchase.mv_id ?? "—"}`}
                </span>
              ) : (
                <span>—</span>
              )}
            </div>
            <div>
              <span className="text-slate-500">Best viewed unit price (lowest tier): </span>
              {bestViewed != null ? <span>${bestViewed.toFixed(4)}</span> : <span>—</span>}
            </div>
          </div>
          {data.gaps.length > 0 ? (
            <ul className="list-inside list-disc text-sm text-amber-900">
              {data.gaps.map((gap) => (
                <li key={gap}>{gap}</li>
              ))}
            </ul>
          ) : null}
          <div className="border-t border-slate-100 pt-3">
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={
                  discoverDistributorLinksMutation.isPending ||
                  (shoppingQuery.data?.aml_rows.length ?? 0) === 0 ||
                  !hubWritesAllowed
                }
                onClick={() => {
                  if (!hubWritesAllowed) return;
                  discoverDistributorLinksMutation.mutate();
                }}
              >
                {discoverDistributorLinksMutation.isPending
                  ? "Querying distributors…"
                  : "Discover Digi-Key / Mouser / Future links"}
              </Button>
            </ReadOnlyHoverLayer>
            <p className="mt-2 text-xs text-slate-500">
              For each approved alternate with an MPN, calls the DigiKey and Mouser product APIs when keys are saved
              under Admin → Distributor APIs, then Octopart for Future Electronics and other seller URLs. Adds matching
              Megaventory suppliers to sourcing, fills empty product URLs, seeds price breaks when none exist, and turns
              on the Octopart column when Octopart matches. Requires DigiKey/Mouser keys and/or{" "}
              <code className="rounded bg-slate-100 px-1">OCTOPART_API_TOKEN</code> on the server.
            </p>
            {discoverDistributorLinksMutation.isError ? (
              <p className="mt-2 text-sm text-red-600">
                {discoverDistributorLinksMutation.error instanceof Error
                  ? discoverDistributorLinksMutation.error.message
                  : "Discover failed."}
              </p>
            ) : null}
          </div>
        </CardBody>
      </Card>

      <ShoppingSourcingMatrixTable
        cpn={data.cpn}
        amlRows={amlSorted}
        amlSuppliers={data.aml_suppliers}
        amlShoppingPrefs={data.aml_shopping_prefs ?? []}
        listings={data.listings}
        setEditKey={setEditKey}
        setDraft={setDraft}
        copyText={copyText}
        hubWritesAllowed={hubWritesAllowed}
        hubReadOnlyTitle={mrpReadOnlyTitle}
        stageLine={(body) => {
          if (!hubWritesAllowed) return;
          postStagedMutation.mutate(body);
        }}
      />

      {editKey != null && draft != null ? (
        <ListingEditorPanel
          draft={draft}
          setDraft={setDraft}
          onCancel={() => {
            setEditKey(null);
            setDraft(null);
          }}
          onSave={() => {
            if (!hubWritesAllowed) return;
            const match = /^(\d+)-(\d+)$/.exec(editKey);
            if (!match || !draft) return;
            const approved_component_id = Number.parseInt(match[1], 10);
            const supplier_data_id = Number.parseInt(match[2], 10);
            putListingMutation.mutate({
              approved_component_id,
              supplier_data_id,
              product_url: draft.product_url.trim() === "" ? null : draft.product_url.trim(),
              purchaser_notes: draft.purchaser_notes.trim() === "" ? null : draft.purchaser_notes.trim(),
              is_preferred_for_buy: draft.is_preferred_for_buy,
              offers_product_observed_at:
                draft.offers_product_observed_at.trim() === ""
                  ? null
                  : new Date(draft.offers_product_observed_at).toISOString(),
              availability_note: draft.availability_note.trim() === "" ? null : draft.availability_note.trim(),
              price_breaks: draft.price_breaks.map((tier) => ({
                break_quantity: tier.break_quantity,
                unit_price: tier.unit_price,
                currency: tier.currency,
                observed_at: new Date(tier.observed_at).toISOString(),
                note: tier.note,
              })),
            });
          }}
          busy={putListingMutation.isPending}
          hubWritesAllowed={hubWritesAllowed}
          hubReadOnlyTitle={mrpReadOnlyTitle}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitleWithHelp hoverHint="Same supplier only. Right-click a supplier cell in the sourcing matrix and choose Stage line, then open create PO.">
            Staged for purchase order
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody className="space-y-2">
          {stagedQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading staged lines…</p>
          ) : (stagedQuery.data?.rows.length ?? 0) === 0 ? (
            <p className="text-sm text-slate-600">No staged lines.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {stagedQuery.data!.rows.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-2"
                >
                  <span className="font-mono text-xs">
                    {row.cpn} · {row.mv_supplier_name ?? `supplier ${row.supplier_data_id}`} · qty {row.quantity}
                    {row.unit_price != null ? ` · $${row.unit_price}` : ""}
                  </span>
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={!hubWritesAllowed}
                      onClick={() => {
                        if (!hubWritesAllowed) return;
                        deleteStagedMutation.mutate(row.id);
                      }}
                    >
                      Remove
                    </Button>
                  </ReadOnlyHoverLayer>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap gap-2">
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
              <Button
                type="button"
                size="sm"
                variant="primary"
                disabled={(stagedQuery.data?.rows.length ?? 0) === 0 || !hubWritesAllowed}
                onClick={() => openPoWithStaged()}
              >
                Open create PO with staged lines
              </Button>
            </ReadOnlyHoverLayer>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={mrpReadOnlyTitle} layout="inline">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={
                  (stagedQuery.data?.rows.length ?? 0) === 0 || clearStagedMutation.isPending || !hubWritesAllowed
                }
                onClick={() => {
                  if (!hubWritesAllowed) return;
                  clearStagedMutation.mutate();
                }}
              >
                Clear staged
              </Button>
            </ReadOnlyHoverLayer>
          </div>
        </CardBody>
      </Card>

      {amlSorted.length === 0 ? (
        <p className="text-sm text-slate-600">
          No AML rows to shop against. Add alternates on the{" "}
          <Link className="text-sky-700 underline hover:text-sky-900" to={buildMrpSkuDetailPath(data.cpn, { tab: "aml" })}>
            AML detail
          </Link>{" "}
          tab.
        </p>
      ) : null}
    </div>
  );
}

function ListingEditorPanel({
  draft,
  setDraft,
  onCancel,
  onSave,
  busy,
  hubWritesAllowed = true,
  hubReadOnlyTitle = "",
}: {
  draft: ListingDraft | null;
  setDraft: (draft: ListingDraft | null) => void;
  onCancel: () => void;
  onSave: () => void;
  busy: boolean;
  hubWritesAllowed?: boolean;
  hubReadOnlyTitle?: string;
}) {
  if (!draft) return null;

  const readOnlyListingMessage =
    (hubReadOnlyTitle ?? "").trim() !== "" ? hubReadOnlyTitle : "Your role has read-only access to the MRP hub module.";

  const updateTier = (index: number, patch: Partial<Omit<MrpShoppingPriceBreak, "id">>): void => {
    setDraft({
      ...draft,
      price_breaks: draft.price_breaks.map((tier, tierIndex) =>
        tierIndex === index ? { ...tier, ...patch } : tier,
      ),
    });
  };

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/80 p-4">
      <h4 className="text-sm font-semibold text-slate-900">Edit listing</h4>
      <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyListingMessage} layout="block">
      <fieldset disabled={!hubWritesAllowed} className="min-w-0 border-0 p-0 m-0 space-y-3 disabled:opacity-90">
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div className="md:col-span-2">
          <Label>Product URL (https)</Label>
          <Input
            value={draft.product_url}
            onChange={(event) => setDraft({ ...draft, product_url: event.target.value })}
            placeholder="https://…"
          />
        </div>
        <div className="md:col-span-2">
          <Label>Purchaser notes</Label>
          <Input
            value={draft.purchaser_notes}
            onChange={(event) => setDraft({ ...draft, purchaser_notes: event.target.value })}
          />
        </div>
        <div>
          <Label>Offers product observed at</Label>
          <Input
            type="datetime-local"
            value={draft.offers_product_observed_at}
            onChange={(event) => setDraft({ ...draft, offers_product_observed_at: event.target.value })}
          />
        </div>
        <div>
          <Label>Availability note</Label>
          <Input
            value={draft.availability_note}
            onChange={(event) => setDraft({ ...draft, availability_note: event.target.value })}
          />
        </div>
        <div className="md:col-span-2 flex items-center gap-2">
          <input
            id="pref-buy"
            type="checkbox"
            checked={draft.is_preferred_for_buy}
            onChange={(event) => setDraft({ ...draft, is_preferred_for_buy: event.target.checked })}
          />
          <Label htmlFor="pref-buy">Preferred for buy (this alternate + supplier)</Label>
        </div>
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-800">Price tiers (viewed)</span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setDraft({ ...draft, price_breaks: [...draft.price_breaks, emptyTier()] })}
          >
            Add tier
          </Button>
        </div>
        <div className="mt-2 space-y-2">
          {draft.price_breaks.map((tier, index) => (
            <div key={index} className="grid gap-2 rounded border border-slate-200 bg-white p-2 sm:grid-cols-5">
              <div>
                <Label>Break qty</Label>
                <Input
                  type="number"
                  min={1}
                  value={tier.break_quantity}
                  onChange={(event) =>
                    updateTier(index, { break_quantity: Number.parseInt(event.target.value, 10) || 1 })
                  }
                />
              </div>
              <div>
                <Label>Unit price</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.0001"
                  value={tier.unit_price}
                  onChange={(event) =>
                    updateTier(index, { unit_price: Number.parseFloat(event.target.value) || 0 })
                  }
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Input
                  value={tier.currency}
                  onChange={(event) => updateTier(index, { currency: event.target.value })}
                />
              </div>
              <div>
                <Label>Observed at</Label>
                <Input
                  type="datetime-local"
                  value={tier.observed_at}
                  onChange={(event) => updateTier(index, { observed_at: event.target.value })}
                />
              </div>
              <div className="flex items-end gap-1">
                <div className="min-w-0 flex-1">
                  <Label>Note</Label>
                  <Input
                    value={tier.note ?? ""}
                    onChange={(event) => updateTier(index, { note: event.target.value || null })}
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={draft.price_breaks.length <= 1}
                  onClick={() =>
                    setDraft({
                      ...draft,
                      price_breaks: draft.price_breaks.filter((_, tierIndex) => tierIndex !== index),
                    })
                  }
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      </fieldset>
      </ReadOnlyHoverLayer>
      <div className="mt-4 flex flex-wrap gap-2">
        <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={readOnlyListingMessage} layout="inline">
          <Button type="button" size="sm" variant="primary" disabled={busy || !hubWritesAllowed} onClick={onSave}>
            {busy ? "Saving…" : "Save listing"}
          </Button>
        </ReadOnlyHoverLayer>
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
